import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import { useSnackbar } from 'notistack';
import env from '../config/env';

function Copyright() {
  const { t } = useTranslation();
  
  return (
    <div className="text-sm text-gray-500 text-center">
      {t('login.copyright')} {new Date().getFullYear()}
      {' - '}
      {t('login.poweredBy')}{' '}
      <a 
        href="https://aceroyalty.com/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-primary-600 hover:text-primary-700 transition-colors"
      >
        {t('login.aceroyalty')}
      </a>
    </div>
  );
}

const Login = ({ onLoginSuccess }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasProcessedAuth = useRef(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setLanguageMenuAnchor(null);
  };

  useEffect(() => {
    // Evitar procesamiento múltiple en React StrictMode
    if (hasProcessedAuth.current) {
      return;
    }

    // Verificar si hay un token de Azure AD en la URL
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    const idToken = urlParams.get('id_token');
    const accessToken = urlParams.get('access_token');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      setError(`${t('errors.loginFailed')}: ${errorDescription || error}`);
      hasProcessedAuth.current = true;
      return;
    }

    if (idToken && state === 'login') {
      hasProcessedAuth.current = true;
      handleAzureLogin(idToken, accessToken);
    }
  }, [t]);

  const handleAzureLogin = async (idToken, accessToken) => {
    setLoading(true);
    setError(null);

    try {
      const response = await AuthService.LoginAzureAd({
        password: idToken,
        username: 'ad',
        azureAccessToken: accessToken
      });

      if (response.status >= 200 && response.status < 300) {
        
        // Obtener las empresas permitidas después del login exitoso (OBLIGATORIO)
        try {
          const companiesResponse = await AuthService.GetAllowedCompanies();
          
          localStorage.setItem("allowedCompanies", JSON.stringify(companiesResponse.data));
        } catch (error) {
          console.error('CRITICAL ERROR fetching allowed companies:', error);
          // Si falla la obtención de empresas, mostrar error
          throw new Error(`Error obteniendo empresas permitidas: ${error.message}`);
        }
        
        // Removido el mensaje de éxito como solicitado
        onLoginSuccess?.();
        
        // Limpiar la URL después del login exitoso
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Redirigir al dashboard
        navigate('/');
      } else {
        throw new Error(response?.data?.message || t('errors.loginFailed'));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t('errors.unknownError');
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    setLoading(true);
    setError(null);

    // Resetear la referencia para permitir nuevo procesamiento
    hasProcessedAuth.current = false;

    // Construir URL de Microsoft OAuth
    const clientId = env.azureAppClientId;

    if (!clientId) {
      setError(t('errors.azureConfigError'));
      setLoading(false);
      return;
    }

    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent('openid profile email User.Read');
    const responseType = 'id_token token';
    const responseMode = 'fragment';
    const state = 'login';

    // Generar nonce aleatorio para seguridad
    const nonce = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    const microsoftUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=${responseType}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}&` +
      `response_mode=${responseMode}&` +
      `state=${state}&` +
      `nonce=${nonce}&` +
      `prompt=select_account`;

    window.location.href = microsoftUrl;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card shadow-soft backdrop-blur-soft bg-white/95 border border-white/20 relative overflow-hidden">
          {/* Header gradient line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-600 to-primary-800"></div>
          
          <div className="flex flex-col items-center text-center p-8">
            {/* Logo Section */}
            <div className="mb-6 animate-fade-in">
              <div className="w-20 h-20 mb-4 rounded-lg overflow-hidden shadow-lg flex items-center justify-center bg-white border-2 border-gray-100 mx-auto">
                <img
                  src="/logo512.png"
                  alt="ACE VAULT Logo"
                  className="w-full h-full object-contain block"
                />
              </div>

              <h1 className="text-3xl font-bold text-gray-800 mb-2 tracking-wide">
                {t('login.title')}
              </h1>

              <p className="text-lg text-gray-600 font-normal mb-4 opacity-80">
                {t('login.subtitle')}
              </p>
            </div>



            {/* Error Alert */}
            {error && (
              <div className="w-full mb-6 animate-fade-in">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Office 365 Login Button */}
            <div className="w-full animate-bounce-subtle">
              <button
                onClick={handleMicrosoftLogin}
                disabled={loading}
                className="w-full h-14 text-base font-medium rounded-lg border-2 border-blue-600 bg-white text-blue-600 hover:bg-gray-50 hover:border-blue-700 hover:text-blue-700 active:bg-gray-100 disabled:bg-gray-100 disabled:border-gray-400 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    {/* Microsoft Logo */}
                    <div className="w-5 h-5 flex items-center justify-center mr-3">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="9" height="9" fill="#f25022"/>
                        <rect x="11" width="9" height="9" fill="#7fba00"/>
                        <rect y="11" width="9" height="9" fill="#00a4ef"/>
                        <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                      </svg>
                    </div>
                    <span className="text-base font-medium">MICROSOFT 365</span>
                  </div>
                )}
              </button>
            </div>

            {/* Security Features */}
            <div className="mt-8 p-4 rounded-lg bg-blue-50 border border-blue-100 w-full animate-fade-in">
              <div className="flex items-center justify-center text-sm text-gray-600 opacity-80">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {t('login.securityMessage')}
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-8 animate-fade-in">
              <Copyright />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
